import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import _ from 'lodash'

import DiagnosticStethoscope from '@/pages/diagnostic/components/DiagnosticStethoscope'
import { DiagnosticRTCProvider, DiagnosticRTCServiceType } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'
import { documentOfflineStore, consultationOfflineStore, OfflineConsultation } from '@/core/offline'
import { AudioRecordingData } from '@/components/ui/tables/AudioRecordingTable'
import { RecordingAnnotation } from '@/core/models/document.model'

// Helper function to convert file to base64
const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
            const result = reader.result as string
            // Remove the data URL prefix to get just the base64 data
            resolve(result.split(',')[1])
        }
        reader.onerror = error => reject(error)
    })
}

export type OfflineDiagnosticStethoscopeContainerProps = {
    consultation: OfflineConsultation
    onConsultationUpdate?: (consultation: OfflineConsultation) => void
}

export function OfflineDiagnosticStethoscopeContainer(props: OfflineDiagnosticStethoscopeContainerProps) {
    const { consultation, onConsultationUpdate } = props
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()

    const audioRecordings = useMemo(() => {
        if (!consultation.files) return []

        return _.map(
            _.filter(consultation.files, (file) => {
                // Filter for audio recordings
                return (file.filename.toLowerCase().startsWith('audio') && file.filename.toLowerCase().endsWith('mp3')) || !!file.isRecording
            }),
            (file) => ({
                key: file.key,
                filename: file.filename,
                url: file.url || '', // In offline mode, we might not have URLs
                annotations: file.recordingAnnotations || [],
            })
        )
    }, [consultation.files])

    const patientInfo = useMemo(
        () => ({
            firstName: consultation.patient?.userInfo?.name || '',
            lastName: consultation.patient?.userInfo?.surname || '',
        }),
        [consultation.patient]
    )

    const handleDeleteAnnotation = useCallback(async (annotationId: string, recordingFile: AudioRecordingData) => {
        try {
            // In offline mode, we would need to implement annotation deletion in the offline store
            // For now, we'll show a message that this feature is not available offline
            enqueueSnackbar('Annotation deletion is not available in offline mode', { variant: 'info' })
        } catch (error) {
            console.error('Error deleting annotation:', error)
            enqueueSnackbar(t('notification.recordingAnnotation.delete.error'), { variant: 'error' })
        }
    }, [enqueueSnackbar, t])

    const handleUploadAudioRecording = useCallback(async (file: File) => {
        if (!consultation._id) return

        try {
            // Convert file to base64
            const base64Data = await fileToBase64(file)

            // Create offline document for audio recording
            await documentOfflineStore.createOfflineDocument({
                filename: file.name,
                size: file.size,
                mimeType: file.type,
                base64Data: base64Data,
                consultationId: consultation._id
            })

            enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' })

            // Refresh consultation data if callback provided
            if (onConsultationUpdate) {
                // Reload the consultation from the store to get updated files with the new recording
                const updatedConsultation = await consultationOfflineStore.get(consultation._id)
                if (updatedConsultation) {
                    onConsultationUpdate(updatedConsultation)
                }
            }
        } catch (error) {
            console.error('Error uploading audio recording:', error)
            enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' })
        }
    }, [consultation, onConsultationUpdate, enqueueSnackbar, t])

    const handleUpdateAnnotation = useCallback(async (
        annotation: { id: string; start: number; end: number; note: string },
        recordingFile: AudioRecordingData
    ) => {
        try {
            // In offline mode, we would need to implement annotation updates in the offline store
            // For now, we'll show a message that this feature is not available offline
            enqueueSnackbar('Annotation editing is not available in offline mode', { variant: 'info' })
        } catch (error) {
            console.error('Error updating annotation:', error)
            enqueueSnackbar(t('notification.recordingAnnotation.save.error'), { variant: 'error' })
        }
    }, [enqueueSnackbar, t])

    // Configure offline RTC provider
    const rtcConfig = {
        [DiagnosticRTCServiceType.Offline]: {}
    }

    return (
        <DiagnosticRTCProvider
            config={rtcConfig}
            customDevices={[]} // Could be populated with custom stethoscope devices
            isStreamer={true} // In offline mode, we're always the streamer
            type="audio"
            onRecordingStop={handleUploadAudioRecording}
        >
            <DiagnosticStethoscope
                audioRecordings={audioRecordings}
                onRecordStop={handleUploadAudioRecording}
                onDeleteAnnotation={handleDeleteAnnotation}
                onUpdateAnnotation={handleUpdateAnnotation}
                patientInfo={patientInfo}
            />
        </DiagnosticRTCProvider>
    )
}

export default OfflineDiagnosticStethoscopeContainer 